import { useEffect, useRef, useState } from 'react'
import type { AgentOSAvatarRole } from '../lib/voice-agent/AgentOSAvatarRuntime'
import {
  ARCTURIAN_FULLBODY_MANIFEST,
  fullbodyAssetForRole,
  type ArcturianFullbodyAssetRole,
} from '../lib/voice-agent/ArcturianFullbodyManifest'
import { GreenScreenCanvasCrossfader } from '../lib/voice-agent/GreenScreenCanvasCrossfader'

interface Props {
  role: AgentOSAvatarRole
  sessionActive: boolean
  onFailureChange?: (failed: boolean) => void
  onFirstFrameReady?: () => void
  onTransientRoleEnded?: (role: AgentOSAvatarRole) => void
  /** Read-only keyed frame projection for WebXR/CanvasTexture hosts. */
  onFrameCanvasChanged?: (canvas: HTMLCanvasElement | null) => void
}

/** FULLBODY green-screen renderer used only by the explicit Web test variant. */
export function ArcturianFullbodyAvatar({
  role,
  sessionActive,
  onFailureChange,
  onFirstFrameReady,
  onTransientRoleEnded,
  onFrameCanvasChanged,
}: Props) {
  const videoA = useRef<HTMLVideoElement>(null)
  const videoB = useRef<HTMLVideoElement>(null)
  const canvasA = useRef<HTMLCanvasElement>(null)
  const canvasB = useRef<HTMLCanvasElement>(null)
  const player = useRef<GreenScreenCanvasCrossfader | null>(null)
  const firstFrameReported = useRef(false)
  const [failed, setFailed] = useState(false)
  const [hasVisibleFrame, setHasVisibleFrame] = useState(false)

  useEffect(() => {
    const elements = [videoA.current, videoB.current, canvasA.current, canvasB.current]
    if (elements.some((element) => !element)) return
    const renderer = new GreenScreenCanvasCrossfader(
      [
        { video: videoA.current!, canvas: canvasA.current! },
        { video: videoB.current!, canvas: canvasB.current! },
      ],
      {
        width: ARCTURIAN_FULLBODY_MANIFEST.web_runtime.render_width,
        height: ARCTURIAN_FULLBODY_MANIFEST.web_runtime.render_height,
        fadeMs: ARCTURIAN_FULLBODY_MANIFEST.renderer_profiles.web.crossfade_ms,
        key: {
          thresholdOpaque: ARCTURIAN_FULLBODY_MANIFEST.keying.threshold_opaque,
          thresholdTransparent: ARCTURIAN_FULLBODY_MANIFEST.keying.threshold_transparent,
        },
        onVisibleFrameChange: (visible) => {
          setHasVisibleFrame(visible)
          if (visible && !firstFrameReported.current) {
            firstFrameReported.current = true
            onFirstFrameReady?.()
          }
        },
        onFrameCanvasChanged,
        onLoadError: (fatal, src) => {
          if (fatal) {
            setFailed(true)
            setHasVisibleFrame(false)
            onFailureChange?.(true)
          } else {
            console.warn('[ArcturianFullbody] Incoming clip failed; keeping current panel:', src)
          }
        },
      },
    )
    player.current = renderer
    return () => {
      renderer.destroy()
      if (player.current === renderer) player.current = null
    }
  }, [onFailureChange, onFirstFrameReady, onFrameCanvasChanged])

  useEffect(() => {
    const renderer = player.current
    if (!renderer) return
    if (failed || !sessionActive) {
      renderer.stop()
      firstFrameReported.current = false
      return
    }

    const asset = fullbodyAssetForRole(role as ArcturianFullbodyAssetRole)
    const oneShot = role === 'greeting' || role === 'happy' || role === 'joyful' || role === 'sad'
    renderer.setTarget({
      src: asset.url,
      loop: oneShot ? false : asset.loop,
      onEnded: oneShot ? () => onTransientRoleEnded?.(role) : undefined,
    })
  }, [role, sessionActive, failed, onTransientRoleEnded])

  return (
    <div className="tl-fullbody" data-avatar-variant="fullbody" data-avatar-role={role}>
      <img
        className={`tl-fullbody__poster${hasVisibleFrame ? ' tl-fullbody__poster--hidden' : ''}`}
        src={fullbodyAssetForRole('idle').poster}
        alt="Dr. Peter Tschauko als Ganzkörperfigur"
        data-avatar-fallback-visible={hasVisibleFrame ? 'false' : 'true'}
        aria-hidden="true"
      />
      <video
        ref={videoA}
        className="tl-fullbody__source"
        data-avatar-video-panel="0"
        crossOrigin="anonymous"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <video
        ref={videoB}
        className="tl-fullbody__source"
        data-avatar-video-panel="1"
        crossOrigin="anonymous"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <canvas ref={canvasA} className="tl-fullbody__canvas" data-avatar-canvas-panel="0" aria-hidden="true" />
      <canvas ref={canvasB} className="tl-fullbody__canvas" data-avatar-canvas-panel="1" aria-hidden="true" />
    </div>
  )
}
